/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {withTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {useCurrentThemeNavigation} from '@ion/applications/src/navigation/useCurrentThemeNavigation';
import {useRenderedPage} from '@ion/applications/src/navigation/useRenderedPage';
import {translate} from '@ion/core/src/locale/locale';
import {info} from '@ion/core/src/logger/logger';
import {getValidElementId} from '@ion/core/src/utils/StringUtil';
import Navigation, {SideNavProps} from '@ion/dls/src/components/navigation/Navigation';
import {dlsSidebarDrawerLeft, setIsSideNavCollapsed} from '@ion/dls/src/components/navigation/NavigationStore';
import ResultsModule from '@ion/dls/src/components/sidepanel/ResultsModule';
import {getSelectedFilters} from '@ion/dls/src/filters/SelectedFilters';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {SideNavAdminIcon} from '@ion/dls/src/icons/SideNavAdminIcon';
import {SideNavCrashBoxxIcon} from '@ion/dls/src/icons/SideNavCrashBoxxIcon';
import {SideNavDashboardIcon} from '@ion/dls/src/icons/SideNavDashboardIcon';
import {SideNavELDIcon} from '@ion/dls/src/icons/SideNavELDIcon';
import {SideNavEventsIcon} from '@ion/dls/src/icons/SideNavEventsIcon';
import {SideNavInspectionsIcon} from '@ion/dls/src/icons/SideNavInspectionsIcon';
import {SideNavIssuesIcon} from '@ion/dls/src/icons/SideNavIssuesIcon';
import {SideNavMaintenanceIcon} from '@ion/dls/src/icons/SideNavMaintenanceIcon';
import {SideNavReportsIcon} from '@ion/dls/src/icons/SideNavReportsIcon';
import {SideNavTrackingIcon} from '@ion/dls/src/icons/SideNavTrackingIcon';
import {SideNavTripListIcon} from '@ion/dls/src/icons/SideNavTripListIcon';
import {SideNavVisionIcon} from '@ion/dls/src/icons/SideNavVisionIcon';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import Render from '@ion/dls/src/render/Render';
import useReseller from '@ion/dls/src/theme/resellers/useReseller';
import {dlsColor, dlsLayout, dlsZIndex} from '@ion/dls/src/theme/theme';
import {DLSThemes} from '@ion/dls/src/theme/types';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {MenuItem} from '@ion/dls/src/types/components';
import {TrackingMode} from '@ion/dls/src/types/types';
import ContactSupport from '@ion/features/src/support/ContactSupport';
import changePage, {
  getFeatureByRoute,
  getRoute,
  getRouteDefinition,
  getRouteLabel,
  Route
} from '@/routes/RouteDefinitions';
import {getDefaultPage} from '@/routes/RouteSecurity';
import Features, {TRACKING_PAGE2} from '@/security/Features';
import {
  setActiveIndex,
  setIgnoreQuery,
  setLayoutOffsetHeight,
  setSideSheetStatus,
  toggleSecondaryNav
} from '@/store/actions/nav';
import {
  findNearby,
  refreshLargeFleetAssets,
  setFindButtonClickStatus,
  setFindNearbyPopup
} from '@/store/actions/tracking';
import {authorizedPages} from '@/store/reducers/nav';
import {
  globalGroupTitleSelector,
  isViewingglobalGroupSelector
} from '@/store/selectors/global-group-chooser-selectors';
import {
  activeIndexSelector,
  currentPageSelector,
  gridOptionsSelector,
  isUserProfileViewSelector,
  mobileSideSheetOpenSelector,
  pagesSelector,
  secondaryNavSelector,
  selectedIndexSelector,
  sideSheetSelector
} from '@/store/selectors/nav-selectors';
import {
  boundaryModeSelector,
  findButtonStateSelector,
  findNearbyIsActiveSelector,
  findNearbyV2IsActiveSelector,
  geofenceFindNearbySelector,
  isLargeFleetModeSelector,
  isMobileFilterActiveSelector,
  isTrackingInitializedSelector,
  locationFindNearbySelector,
  mapInitializedSelector,
  scDevicesSelector,
  selectedAssetSelector,
  showPopupFindNearbyStateSelector,
  sideSheetViewSelector,
  trackingFiltersSelector,
  trackingModeSelector,
  typeFindNearbySelector
} from '@/store/selectors/tracking-selectors';
import {Page} from '@/types/pages';
import {
  getTrackingFilterOpenRequested,
  setTrackingFilterOpenRequested,
  setTrackingSearchOverlayOpen,
  subscribeTrackingFilterOpenRequested
} from '../components/filter/current/store';
import TrackingFilterDrawer from '../components/filter/current/TrackingFilterDrawer';
import {TrackingFilterDrawerHandle} from '../components/filter/current/types';
import DesktopUserProfileView from '../header/DesktopUserProfileView';
import GlobalGroupChooserDrawer from '../header/GlobalGroupChooserDrawer';
import {useAssetlength} from '../routes/tracking/hooks/sidesheet/useAssetlength';
import {getAppliedTrackingFilterCount} from '../routes/tracking2/helpers';
import {usernameSelector} from '../store/selectors/login-selectors';
import {
  isPanelOpenSelector,
  unreadCountSelector
} from '../store/selectors/notifications-selectors';
import useCurrentNavigation from './current/useCurrentNavigation';
import useSideNavDrawers, {SideNavDrawer} from './current/useSideNavDrawers';
import useTrackingFilterLeaveGuard from './current/useTrackingFilterLeaveGuard';
import SideSheetContent from './SideSheetContent';
import styles from './SideNav.module.scss';

// the override styles only lift the header's labels in the current theme ...
interface OverrideStyleProps {
  isCurrent: boolean;
}

// defines the override styes if applicable for any navigation item.
const useOverrideStyles = makeStyles(theme => ({
  sideSheetArrow : {
    top : '64px'
  },
  collapsedTooltip : {
    backgroundColor : 'transparent',
    marginLeft      : '8px !important',
    maxWidth        : 'none',
    padding         : '0 !important'
  },
  collapsedTooltipPopper : {
    pointerEvents : 'none',
    zIndex        : ({isCurrent}: OverrideStyleProps) => (
      isCurrent ? `${dlsZIndex('navigationFlyout')} !important` : undefined
    )
  }
}));

/**
 * Provides the mapping of ION page navigation items to their respective
 * DLS Icons.
 */
const pageIconMap = {
  [Route.FLEET_DASHBOARD] : SideNavDashboardIcon,
  dashboard               : SideNavDashboardIcon,
  tracking                : SideNavTrackingIcon,
  tracking2               : SideNavTrackingIcon,
  [Route.TRIP]            : SideNavTripListIcon,
  [Route.VISION]          : SideNavVisionIcon,
  issues                  : SideNavIssuesIcon,
  reports                 : SideNavReportsIcon,
  [Route.ANALYTICS]       : SideNavReportsIcon,
  crashboxx               : SideNavCrashBoxxIcon,
  maintenance             : SideNavMaintenanceIcon,
  [Route.INSPECTIONS]     : SideNavInspectionsIcon,
  events                  : SideNavEventsIcon,
  admin                   : SideNavAdminIcon,
  eld                     : SideNavELDIcon
};

/**
 * @setIcon
 *
 * Returns the icon component for the side nav with the correct
 * coloring based on the current selected page.
 */
const setIcon = (page: string, currentPage: string, variant: DLSThemes = DLSThemes.legacy) => {
  const Icon = pageIconMap[page];
  if (!Icon) {
    return null;
  }

  const selected = currentPage?.includes(page);
  const color = variant === DLSThemes.current
    ? (selected ? dlsColor('aaBlue') : dlsColor('grey1'))
    : (selected ? dlsColor('grey2') : dlsColor('white'));

  return <Icon color={color} />;
};

// the current theme's menu groups that open their only page straight away -
// Trip and its Trip List - rather than a submenu of one ...
const FLATTENED_CURRENT_ROUTES: string[] = [Route.TRIP];

const isTrackingRoute = (route?: string) => (
  route === Route.TRACKING || route === Route.TRACKING2
);

const isTrackingPage = (page?: string) => (
  page === getRoute(Route.TRACKING) || page === getRoute(Route.TRACKING2)
);

const TRACKING_FILTER_DRAWER_OPEN_TIMEOUT  = 15000;
const TRACKING_SEARCH_OVERLAY_OPEN_TIMEOUT = 15000;

export const getAssetCountGridFiltered = (assets: any[], filterAssetIds: number[]) => {
  return filterAssetIds
    ? assets?.filter(asset => filterAssetIds?.includes(asset.id()))?.length
    : assets?.length;
};

// we only initialize the routeChangeComplete event once for the life of the session ...
// let routerInitialized = false;

/**
 * @SideNav
 *
 * The SideNav component provides the ION Application Side Navigation UI.
 */
export const SideNav: FC = ({onToggleCollapse}: SideNavProps) => {
  const {isCurrent, multiThemeEnabled} = useThemeContext();

  const overrideStyle           = useOverrideStyles({isCurrent});
  const pages                   = useSelector(pagesSelector);
  const appEntryPage            = useSelector(({nav}: any) => nav.appEntryPage);
  const index                   = useSelector(selectedIndexSelector);
  const activeIndex             = useSelector(activeIndexSelector);
  const currentPage             = useSelector(currentPageSelector);
  const secondaryNav            = useSelector(secondaryNavSelector);
  const mobileSideSheetOpen     = useSelector(mobileSideSheetOpenSelector);
  const sideSheetStatus         = useSelector(sideSheetSelector);
  const isUserProfileView       = useSelector(isUserProfileViewSelector);
  const showFindNearByPopup     = useSelector(showPopupFindNearbyStateSelector);
  const findButtonStatus        = useSelector(findButtonStateSelector);
  const isInitialized           = useSelector(isTrackingInitializedSelector);
  const isLoaded                = useSelector(mapInitializedSelector);
  const assetData               = useSelector(selectedAssetSelector);
  const geofenceCoordinates     = useSelector(geofenceFindNearbySelector);
  const locationCoordinates     = useSelector(locationFindNearbySelector);
  const typeToFindBy            = useSelector(typeFindNearbySelector);
  const sideSheetView           = useSelector(sideSheetViewSelector);
  const isMobileFilterActive    = useSelector(isMobileFilterActiveSelector);
  const scDevices               = useSelector(scDevicesSelector);
  const isLargeFleetMode        = useSelector(isLargeFleetModeSelector);
  const gridOptions             = useSelector(gridOptionsSelector);
  const trackingMode            = useSelector(trackingModeSelector);
  const globalGroupTitle        = useSelector(globalGroupTitleSelector);
  const isViewingGlobalGroup    = useSelector(isViewingglobalGroupSelector);
  const unreadCount             = useSelector(unreadCountSelector);
  const isNotificationsOpen     = useSelector(isPanelOpenSelector);
  const username                = useSelector(usernameSelector);
  const trackingFilters         = useSelector(trackingFiltersSelector);
  const boundaryMode            = useSelector(boundaryModeSelector);
  const findNearbyIsActive      = useSelector(findNearbyIsActiveSelector);
  const findNearbyV2IsActive    = useSelector(findNearbyV2IsActiveSelector);
  const dispatch                = useDispatch();
  const {isDesktop}             = useMedia();

  const {isReseller}                            = useReseller();
  const [listView, setListView]                 = useState(false);
  const [listAssets, setListAssets]             = useState([]);
  const [sortDescending, setSortDescending]     = useState(true);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [trackingFilterOpenPending, setTrackingFilterOpenPending] = useState(false);
  const [trackingFilterOpenError, setTrackingFilterOpenError]     = useState(false);
  const [trackingSearchOpenPending, setTrackingSearchOpenPending] = useState(false);
  const [trackingSearchOpenError, setTrackingSearchOpenError]     = useState(false);
  const [navigationCollapsed, setNavigationCollapsed]             = useState(false);

  const resultModuleLabel    = useAssetlength(isLargeFleetMode, scDevices, sideSheetView);
  const isMobileDrawerActive = trackingMode === TrackingMode.Live;

  const activeTrackingFilterCount = useMemo(() => (
    // counted as the applied filter bar counts its chips, so the two agree.
    // The current theme's bar shows no Date chip - a History range is its
    // History chip, a mode chip the count leaves out - so none is counted ...
    getAppliedTrackingFilterCount({
      selectedCount      : getSelectedFilters(trackingFilters ?? {}).length,
      boundary           : trackingFilters?.boundary,
      boundaryMode,
      isFindNearbyActive : findNearbyIsActive || findNearbyV2IsActive
    })
  ), [trackingFilters, boundaryMode, findNearbyIsActive, findNearbyV2IsActive]);

  const trackingPageSelected = isTrackingPage(currentPage) || filterDrawerOpen ||
    trackingFilterOpenPending || trackingSearchOpenPending;

  // just past the nav's edge line, so it stays in view beside the drawer ...
  const trackingFilterDrawerOffset = dlsSidebarDrawerLeft(navigationCollapsed);

  const toggleSideSheet = useCallback(function({page, desktopStatus = false, mobileStatus = false}) {
    dispatch(setSideSheetStatus({
      activeSideSheet      : page,
      desktopSideSheetOpen : desktopStatus,
      mobileSideSheetOpen  : mobileStatus
    }));
  }, []);

  const onRefreshAssets= useCallback(function() {
    dispatch(refreshLargeFleetAssets(true) as any);
  }, []);

  // toggles the asset list to list or expanded view
  const toggleListView = useCallback(function() {
    setListView(!listView);
  }, [listView]);

  const mobileToggleSheet = useCallback(function() {
    dispatch(setSideSheetStatus({
      activeSideSheet     : getRoute(Route.TRACKING2),
      mobileSideSheetOpen : false
    }));
  }, []);

  // flips the order of the assets in the list based on Asset Name
  const onSort = useCallback(function() {
    setListAssets(listAssets => {
      const sortedList = listAssets.slice(0).sort(({assetName}, {assetName: name}) => {
        return sortDescending
          ? ((name < assetName) ? -1 : (name > assetName) ? 1 : 0)
          : ((assetName < name) ? -1 : (assetName > name) ? 1 : 0);
      });
      setSortDescending(!sortDescending);
      return sortedList;
    });
  }, [listAssets, sortDescending]);

  const onFindNearyBy = useCallback(function(value) {
    dispatch(findNearby(value) as any);
  }, []);

  const onFindNearbyPopupStatusChange = useCallback(function(status) {
    dispatch(setFindNearbyPopup(status));
  }, []);

  const onFindButtonStateChange = useCallback(function(status) {
    dispatch(setFindButtonClickStatus(status));
  }, []);

  /**
 * @getPages
 *
 * Given the pages state from the nav reducer, returns a list of menu items
 * based on the pages to which the user is authorized to access.
 */
  const getPages = useCallback((pages: Page[], currentPage: string, variant: DLSThemes = DLSThemes.legacy) => {
    const result = authorizedPages(pages).map((page: Page) => {
      const {route, labelId} = page;
      const label = getRouteLabel(route, false, labelId);
      const entry = {
        page : `/${route}`,
        icon : setIcon(route, currentPage, variant),
        key  : `${label}-${getValidElementId()}`,
        label,
        ...page
      };
      if (page.pages) {
        const childPages            = getPages((page as any).pages, currentPage, variant);
        const hasOnlyDuplicateChild = variant === 'current' && (
          childPages?.length === 1 && childPages[0]?.route === entry.route
        );
        const isFlattened           = variant === 'current' && childPages?.length === 1 &&
          FLATTENED_CURRENT_ROUTES.includes(entry.route);
        if (hasOnlyDuplicateChild) {
          delete entry.pages;
          entry.secondaryNav = false;
        } else if (isFlattened) {
          // the group's own icon, leading straight to its only page ...
          const [childPage] = childPages;
          delete entry.pages;
          Object.assign(entry, {
            id           : childPage.id,
            label        : childPage.label,
            page         : childPage.page,
            route        : childPage.route,
            secondaryNav : false
          });
        } else {
          entry.pages = childPages;
        }
      }
      return entry;
    });
    return result;
  }, []);

  const navigationVariant = isCurrent ? 'current' : 'legacy';

  const menuItems = useMemo(() => {
    const authorizedMenuItems = getPages(pages, currentPage, navigationVariant);
    if (navigationVariant !== DLSThemes.current) {
      return authorizedMenuItems;
    }
    return authorizedMenuItems.filter(({route}) => !isTrackingRoute(route));
  }, [getPages, navigationVariant, pages, currentPage]);

  const selectedIndex = useMemo(() => {
    if (!isCurrent) {
      return index;
    }
    return menuItems.findIndex(({page, pages}) => (
      page === currentPage ||
      pages?.some(({page}) => !!page && (page === currentPage || currentPage?.startsWith(`${page}/`)))
    ));
  }, [currentPage, index, isCurrent, menuItems]);

  const activeSubmenuIndex = useMemo(() => {
    return menuItems.findIndex(({pages}) => pages?.some(({page}) => page === currentPage));
  }, [currentPage, menuItems]);

  const getFirstAvailablePage = useCallback((items: MenuItem[]): string => {
    for (const item of items) {
      if (item.page) {
        return item.page;
      }
      const childPage = item.pages?.length ? getFirstAvailablePage(item.pages) : null;
      if (childPage) {
        return childPage;
      }
    }
  }, []);

  // the page the logo leads to - the secured default page, or the first
  // page the user may open when its feature is not available ...
  const getAppEntryPage = useCallback((): string | undefined => {
    const securedDefaultPage = getDefaultPage(getRoute(appEntryPage));
    const defaultFeature     = getFeatureByRoute(getRouteDefinition(securedDefaultPage));

    return Features.hasFeature(defaultFeature)
      ? securedDefaultPage
      : getFirstAvailablePage(menuItems);
  }, [appEntryPage, getFirstAvailablePage, menuItems]);

  const navigateToAppEntry = useCallback((appEntryPage?: string) => {
    if (appEntryPage) {
      info('page changing to app entry screen:', appEntryPage);
      const includeQueryParams = (
        appEntryPage === getRoute(Route.TRACKING) || appEntryPage === getRoute(Route.REPORTS)
      );
      changePage(appEntryPage as Route, undefined, includeQueryParams);
    }
  }, []);

  const navigateToTracking2 = useCallback(() => {
    info('page changing to:', Route.TRACKING2);
    changePage(Route.TRACKING2, undefined, false);
  }, []);

  const closeTrackingFilterDrawer = useCallback(() => {
    setFilterDrawerOpen(false);
    setTrackingFilterOpenPending(false);
  }, []);

  // while the Tracking filter drawer is open with unapplied changes, a
  // navigation away from Tracking waits on its Leave Tracking prompt, and
  // an action that only closes the drawer waits on its Apply/Discard
  // prompt (current theme only) ...
  const trackingFilterDrawerRef      = useRef<TrackingFilterDrawerHandle>(null);
  const {confirmClose, confirmLeave} = useTrackingFilterLeaveGuard({
    drawerOpen : filterDrawerOpen,
    drawerRef  : trackingFilterDrawerRef,
    enabled    : isCurrent
  });

  // every drawer launched from the side nav goes through this one hook, so
  // each collapses an expanded nav before it opens, only one is open at a
  // time, and a side nav item click closes whichever one is open ...
  const {
    closeDrawers,
    closeProfile,
    forceCollapsed,
    isProfileOpen,
    openGroupChooser: openGroupChooserDrawer,
    openProfile: openProfileDrawer,
    toggleNotifications: toggleNotificationsDrawer
  } = useSideNavDrawers({
    dispatch,
    isCurrent,
    isNotificationsOpen,
    isViewingGlobalGroup,
    navigationCollapsed,
    onCloseTrackingFilter : closeTrackingFilterDrawer,
    trackingFilterActive  : filterDrawerOpen || trackingFilterOpenPending
  });

  // the drawers launched from the header close the Tracking filter drawer
  // while staying on Tracking, so they wait on its Apply/Discard prompt ...
  const openGroupChooser = useCallback(function() {
    confirmClose(openGroupChooserDrawer);
  }, [confirmClose, openGroupChooserDrawer]);

  const openProfile = useCallback(function() {
    confirmClose(openProfileDrawer);
  }, [confirmClose, openProfileDrawer]);

  const toggleNotifications = useCallback(function() {
    confirmClose(toggleNotificationsDrawer);
  }, [confirmClose, toggleNotificationsDrawer]);

  const openTrackingFromNavigation = useCallback(function() {
    confirmClose(function() {
      closeDrawers();
      navigateToTracking2();
    });
  }, [closeDrawers, confirmClose, navigateToTracking2]);

  // the logo leaves Tracking unless Tracking is the app entry page, in
  // which case it only closes the Tracking filter drawer ...
  const openAppEntryFromNavigation = useCallback(function() {
    const appEntryPage = getAppEntryPage();
    const confirm      = isTrackingPage(appEntryPage) ? confirmClose : confirmLeave;
    confirm(function() {
      closeDrawers();
      navigateToAppEntry(appEntryPage);
    });
  }, [
    closeDrawers,
    confirmClose,
    confirmLeave,
    getAppEntryPage,
    navigateToAppEntry
  ]);

  const dismissTrackingFilterOpenError = useCallback(() => {
    setTrackingFilterOpenError(false);
  }, []);

  const dismissTrackingSearchOpenError = useCallback(() => {
    setTrackingSearchOpenError(false);
  }, []);

  const handleNavigationCollapseChange = useCallback((event, collapsed: boolean) => {
    setNavigationCollapsed(collapsed);
    setIsSideNavCollapsed(collapsed);
    onToggleCollapse?.(event, collapsed);
  }, [onToggleCollapse]);

  const collapseNavigationForTrackingFilter = useCallback(() => {
    dispatch(toggleSecondaryNav(false));
    dispatch(setActiveIndex(-1));
    setTrackingFilterOpenPending(true);
  }, [dispatch]);

  const openTrackingFilterDrawer = useCallback(() => {
    closeDrawers(SideNavDrawer.TrackingFilter);
    collapseNavigationForTrackingFilter();
    setTrackingFilterOpenError(false);
    if (!isTrackingPage(currentPage)) {
      navigateToTracking2();
    }
  }, [
    closeDrawers,
    collapseNavigationForTrackingFilter,
    currentPage,
    navigateToTracking2
  ]);

  const openTrackingSearch = useCallback(function() {
    confirmClose(function() {
      closeDrawers();
      setTrackingSearchOpenError(false);
      setTrackingSearchOverlayOpen(true);
      if (!isTrackingPage(currentPage)) {
        setTrackingSearchOpenPending(true);
        navigateToTracking2();
      } else {
        setTrackingSearchOpenPending(false);
      }
    });
  }, [closeDrawers, confirmClose, currentPage, navigateToTracking2]);

  const retryOpenTrackingSearch = useCallback(() => {
    openTrackingSearch();
  }, [openTrackingSearch]);

  const retryOpenTrackingFilterDrawer = useCallback(() => {
    openTrackingFilterDrawer();
  }, [openTrackingFilterDrawer]);

  // the drawer may be asked for from outside the navigation - the map's
  // Time Mode limit error among them - and is opened through the same path
  // the filter button takes, collapsing the navigation first, before the
  // request is cleared. The signal calls back as soon as it is subscribed
  // to, so a request made before this mounted is answered too ...
  useEffect(() => (
    subscribeTrackingFilterOpenRequested(function() {
      if (getTrackingFilterOpenRequested()) {
        setTrackingFilterOpenRequested(false);
        openTrackingFilterDrawer();
      }
    })
  ), [openTrackingFilterDrawer]);

  useEffect(() => {
    if (trackingFilterOpenPending && isTrackingPage(currentPage) && navigationCollapsed) {
      setFilterDrawerOpen(true);
      setTrackingFilterOpenPending(false);
      setTrackingFilterOpenError(false);
    }
  }, [currentPage, navigationCollapsed, trackingFilterOpenPending]);

  // the safety net for a page change away from Tracking that was not asked
  // about first - browser back/forward, or an automatic redirect - which
  // discards the drawer's unapplied changes and closes it ...
  useEffect(() => {
    if (filterDrawerOpen && !isTrackingPage(currentPage)) {
      trackingFilterDrawerRef.current?.discardChanges();
      closeTrackingFilterDrawer();
    }
  }, [closeTrackingFilterDrawer, currentPage, filterDrawerOpen]);

  useEffect(() => {
    if (trackingSearchOpenPending && isTrackingPage(currentPage)) {
      setTrackingSearchOpenPending(false);
      setTrackingSearchOpenError(false);
    }
  }, [currentPage, trackingSearchOpenPending]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (trackingFilterOpenPending) {
      timeoutId = setTimeout(() => {
        setTrackingFilterOpenPending(false);
        setTrackingFilterOpenError(true);
      }, TRACKING_FILTER_DRAWER_OPEN_TIMEOUT);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [trackingFilterOpenPending]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (trackingSearchOpenPending) {
      timeoutId = setTimeout(() => {
        setTrackingSearchOpenPending(false);
        setTrackingSearchOverlayOpen(false);
        setTrackingSearchOpenError(true);
      }, TRACKING_SEARCH_OVERLAY_OPEN_TIMEOUT);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [trackingSearchOpenPending]);

  const {
    closeContactSupport,
    closeSecondaryNavigation,
    currentThemeNavigationProps,
    isContactSupportOpen,
    markSkipNextRouteSync,
    openContactSupport
  } = useCurrentNavigation({
    activeIndex,
    activeSubmenuIndex,
    currentPage,
    Features,
    dispatch,
    globalGroupTitle,
    isCurrent,
    isDesktop,
    isReseller,
    onToggleNotifications  : toggleNotifications,
    isViewingGlobalGroup,
    multiThemeEnabled,
    onBrandClick           : openAppEntryFromNavigation,
    onOpenProfile          : openProfile,
    openGroupChooser,
    openTracking           : openTrackingFromNavigation,
    openTrackingSearch,
    trackingFilterSelected : filterDrawerOpen || trackingFilterOpenPending ||
      activeTrackingFilterCount > 0,
    trackingSelected         : trackingPageSelected,
    trackingFilterBadgeCount : activeTrackingFilterCount,
    openTrackingFilter       : openTrackingFilterDrawer,
    secondaryNav,
    tooltipClasses           : {
      popper  : overrideStyle.collapsedTooltipPopper,
      tooltip : overrideStyle.collapsedTooltip
    },
    unreadCount
  });

  const onChangePage = useCallback((page: string) => {
    info('page changing to:', page);
    isDesktop && dispatch(toggleSecondaryNav(false));
    changePage(page as Route, undefined, false);
  }, [isDesktop]);

  const navigateToPage = useCallback((page: string) => {
    info('page changing to:', page);
    changePage(page as Route, undefined, false);
  }, []);

  const handleSetActiveIndex = useCallback((index: number) => {
    dispatch(setActiveIndex(index));
  }, [dispatch]);

  const handleSetLayoutOffsetHeight = useCallback((height: number | null) => {
    dispatch(setLayoutOffsetHeight(height));
  }, [dispatch]);

  const handleToggleSecondaryNavigation = useCallback((show: boolean) => {
    dispatch(toggleSecondaryNav(show));
  }, [dispatch]);

  // the page actually rendered by the router - lags currentPage while the
  // target page is still loading, which keeps the top navigation hidden ...
  const renderedPage = useRenderedPage(currentPage);

  const {
    handleCurrentParentWithSecondaryNavigation,
    handleCurrentSecondaryPage,
    handleTopNavigationVisibilityChange
  } = useCurrentThemeNavigation({
    activeIndex,
    currentPage,
    isCurrent,
    isDesktop,
    markSkipNextRouteSync,
    menuItems,
    navigateToPage,
    secondaryNavigationOpen   : secondaryNav?.show,
    setActiveIndex            : handleSetActiveIndex,
    setLayoutOffsetHeight     : handleSetLayoutOffsetHeight,
    toggleSecondaryNavigation : handleToggleSecondaryNavigation
  });

  /**
   * Invoked when an item within the Navigation Side Bar is selected, at which
   * point the route is pushed to next Router if the route is not a secondary
   * navigation item, otherwise the secondary navigation's view state is set
   * to true. Not guarded - see `setPage`.
   */
  // [TODO]: Refactor to parameters object
  // eslint-disable-next-line max-params
  const changeSideNavPage = useCallback(function(page: string, index: number, isSecondaryIndex?: boolean,
    item?: MenuItem, isMainMenu?: boolean) {
    // a side nav item stays clickable while a drawer is open, and closes it
    // on the way to the item's page (a no-op in the legacy theme) ...
    closeDrawers();
    dispatch(setIgnoreQuery(true));
    if (handleCurrentSecondaryPage({index, isSecondaryIndex, page})) {
      return;
    }
    const hasSecondaryItems               = !!item?.pages?.length;
    if (handleCurrentParentWithSecondaryNavigation({index, isSecondaryIndex, item})) {
      return;
    }
    const pageIndex = (secondaryNav?.show && (!isDesktop || isSecondaryIndex)) ? activeIndex : index;
    dispatch(setActiveIndex((isDesktop && pageIndex === activeIndex) ? -1 : pageIndex));
    const routeDefinition              = getRouteDefinition(page);
    const hasTracking2Feature          = Features.hasFeature(TRACKING_PAGE2);
    const shouldUseSecondaryNavigation = !isSecondaryIndex && hasSecondaryItems;
    switch (routeDefinition) {
      case Route.ADD_ONS_VISION:
      case Route.ELD:
      case Route.CRASHBOXX:
      case Route.MAINTENANCE_DASHBOARD:
        if (isMainMenu && (!isCurrent || !hasSecondaryItems)) {
          onChangePage(page);
        } else {
          dispatch(toggleSecondaryNav(activeIndex !== index || !secondaryNav?.show));
        }
        break;
      case Route.ADMIN:
      case Route.EVENTS:
        if (isCurrent && !hasSecondaryItems) {
          onChangePage(page);
        } else {
          dispatch(toggleSecondaryNav(activeIndex !== index || !secondaryNav?.show));
        }
        break;
      case Route.TRACKING:
        if (isSecondaryIndex || !hasTracking2Feature || (isCurrent && !hasSecondaryItems)) {
          onChangePage(page);
        } else {
          dispatch(toggleSecondaryNav(activeIndex !== index || !secondaryNav?.show));
        }
        break;
      case Route.TRACKING2:
        if (isSecondaryIndex) {
          onChangePage(page);
        } else {
          dispatch(toggleSecondaryNav(activeIndex !== index || !secondaryNav?.show));
        }
        break;
      case Route.REPORTS:
      case Route.ANALYTICS:
      case Route.FLEET_DASHBOARD:
      case Route.INSPECTIONS:
      case Route.VISION:
      case Route.TRIP:
        if ((isCurrent ? shouldUseSecondaryNavigation : isSecondaryIndex === false &&
          menuItems.find(({route}) => route === routeDefinition)?.secondaryNav)) {
          dispatch(toggleSecondaryNav(activeIndex !== index || !secondaryNav?.show));
        } else {
          onChangePage(page);
        }
        break;
      default:
        onChangePage(page);
    }
  }, [
    secondaryNav,
    activeIndex,
    closeDrawers,
    currentPage,
    dispatch,
    isCurrent,
    handleCurrentParentWithSecondaryNavigation,
    handleCurrentSecondaryPage,
    isDesktop,
    menuItems
  ]);

  // a side nav item click - even one that only opens a submenu - waits on
  // the Tracking filter drawer's Leave Tracking prompt while the drawer has
  // unapplied changes; leaving replays the click, unguarded ...
  // eslint-disable-next-line max-params
  const setPage = useCallback(function(page: string, index: number, isSecondaryIndex?: boolean,
    item?: MenuItem, isMainMenu?: boolean) {
    confirmLeave(function() {
      changeSideNavPage(page, index, isSecondaryIndex, item, isMainMenu);
    });
  }, [changeSideNavPage, confirmLeave]);

  const getResultsModule = useCallback(() => {
    return (
      <ResultsModule label={resultModuleLabel}
        onView={toggleListView}
        onSort={onSort}
        gridOptions={gridOptions}
        onRefresh={onRefreshAssets}
        isRefreshEnabled={isLargeFleetMode}
        isTableViewEnabled={isDesktop && !isLargeFleetMode}
        mobileSideSheetClose={mobileToggleSheet}
        mobileSideSheetOpen={mobileSideSheetOpen}
        fullHeight />
    );
  }, [
    onSort,
    isDesktop,
    gridOptions,
    toggleListView,
    resultModuleLabel,
    isLargeFleetMode,
    mobileSideSheetOpen
  ]);

  const getSideSheetContent = useCallback(() => (
    <SideSheetContent toggleSideSheet={toggleSideSheet} />
  ), [toggleSideSheet]);

  return (
    <div className={styles.container}>
      <Navigation useExpandCollapseFeature={isDesktop}
        forceCollapsed={forceCollapsed}
        onToggleCollapse={handleNavigationCollapseChange}
        selectedIndex={selectedIndex}
        activeIndex={activeIndex}
        overrideStyle={overrideStyle}
        onSetPage={setPage}
        menuItems={menuItems}
        secondaryNav={secondaryNav}
        getPages={getPages}
        closeSecondaryNavigation={closeSecondaryNavigation}
        currentPage={currentPage}
        onTopNavigationVisibilityChange={handleTopNavigationVisibilityChange}
        renderedPage={renderedPage}
        {...(isCurrent ? currentThemeNavigationProps : {})}
        sideSheetContent={getSideSheetContent}
        sideSheetMobileSwipeUp={getResultsModule}
        toggleSideSheet={toggleSideSheet}
        sideSheetStatus={sideSheetStatus}
        onFindNearyBy={onFindNearyBy}
        onFindNearbyPopupStatusChange={onFindNearbyPopupStatusChange}
        onFindButtonStateChange={onFindButtonStateChange}
        showFindNearByPopup={showFindNearByPopup}
        findButtonStatus={findButtonStatus}
        isUserProfileView={isUserProfileView}
        assetData={assetData}
        isInitialized={isInitialized}
        isLoaded={isLoaded}
        geofenceCoordinates={geofenceCoordinates}
        locationCoordinates={locationCoordinates}
        typeToFindBy={typeToFindBy}
        sideSheetView={sideSheetView}
        isMobileFilterActive={isMobileFilterActive}
        isDrawerActive={isMobileDrawerActive}
        navigationVariant={navigationVariant}
        toolTipPosition={'right'}
        toolTipArrow
        isiOn
        fullHeight />
      <GlobalGroupChooserDrawer onRequestOpen={openGroupChooser} />
      <Render.If condition={isCurrent}>
        <DesktopUserProfileView open={isProfileOpen}
          id='current-nav-profile-drawer'
          onContactSupportOpen={openContactSupport}
          anchorEl={null}
          handleClick={closeProfile}
          username={username} />
      </Render.If>
      <Render.If condition={isCurrent && isContactSupportOpen}>
        <ContactSupport onCancel={closeContactSupport} />
      </Render.If>
      <Render.If condition={isCurrent}>
        <TrackingFilterDrawer ref={trackingFilterDrawerRef}
          open={filterDrawerOpen}
          desktopOffset={trackingFilterDrawerOffset}
          onClose={closeTrackingFilterDrawer} />
      </Render.If>
      <IonSnackbar show={trackingFilterOpenError}
        message={translate('common:errors.genericPage')}
        actionLabel={translate('common:retry')}
        onAction={retryOpenTrackingFilterDrawer}
        onClose={dismissTrackingFilterOpenError}
        type='error'
        autoHideDuration={6000} />
      <IonSnackbar show={trackingSearchOpenError}
        message={translate('common:errors.genericPage')}
        actionLabel={translate('common:retry')}
        onAction={retryOpenTrackingSearch}
        onClose={dismissTrackingSearchOpenError}
        type='error'
        autoHideDuration={6000} />
    </div>
  );
};

export default withTranslation('nav')(memo(SideNav));
